import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { TwoFactorStep } from "@/components/TwoFactorStep";

export default function Login() {
  const { user, setUser, login, error } = useAuth();
  const [mfa, setMfa] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) {
    if (user.role === "steuerberater") return <Navigate to="/steuerberater" replace />;
    return <Navigate to="/" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res?.mfa_required) { setMfa(res); setPassword(""); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img
          src="https://images.unsplash.com/photo-1553755322-56baa43a31d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxnZXJtYW4lMjBmb3Jlc3QlMjBtb3JuaW5nfGVufDB8fHx8MTc4NTY3MjM4MXww&ixlib=rb-4.1.0&q=85"
          alt="Spessart"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/40 backdrop-blur-[1px]" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-sm uppercase tracking-widest opacity-80" style={{fontFamily:"'Work Sans',sans-serif"}}>MainSpessartBlick</div>
          <h1 className="text-4xl lg:text-5xl font-bold mt-2" style={{fontFamily:"'Work Sans',sans-serif"}}>
            Cockpit für Ihre Unterkunft
          </h1>
          <p className="mt-3 max-w-md opacity-90">Gäste, Buchungen, Rechnungen und Abläufe – zentral & elegant an einem Ort.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-background">
        {mfa ? (
          <div className="w-full max-w-md">
            <TwoFactorStep mfa={mfa} onSuccess={(u) => setUser(u)} onCancel={() => setMfa(null)} />
          </div>
        ) : (
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div>
            <div className="text-sm uppercase tracking-widest text-muted-foreground" style={{fontFamily:"'Work Sans',sans-serif"}}>Anmelden</div>
            <h2 className="text-3xl font-bold mt-1" style={{fontFamily:"'Work Sans',sans-serif"}}>Willkommen zurück</h2>
            <p className="text-muted-foreground mt-1">Bitte melden Sie sich an, um fortzufahren.</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email" type="email" required autoComplete="username"
                data-testid="login-email-input"
                value={email} onChange={(e)=>setEmail(e.target.value)}
                className="mt-1.5 rounded-xl h-11"
              />
            </div>
            <div>
              <Label htmlFor="password">Passwort</Label>
              <Input
                id="password" type="password" required autoComplete="current-password"
                data-testid="login-password-input"
                value={password} onChange={(e)=>setPassword(e.target.value)}
                className="mt-1.5 rounded-xl h-11"
              />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 rounded-xl p-3">
              {error}
            </div>
          )}

          <Button
            type="submit" disabled={busy}
            data-testid="login-submit-btn"
            className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90"
          >
            {busy ? "Anmelden…" : "Anmelden"}
          </Button>

          <div className="text-xs text-muted-foreground text-center pt-2">
            Interner Zugang – nur für autorisierte Mitarbeiter.
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
